// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/*
 * AuthShell.css — sign-in, sign-up, OTP and password-reset pages.
 *
 * The design system here is lifted from the marketing site at
 * dev-new.maxelpay.com, whose /login link points at this very app
 * (dev-dashboard.maxelpay.com/login). Someone arriving from the site should not
 * notice a change of product, so the tokens below are its values verbatim,
 * read from its own stylesheets rather than eyeballed:
 *
 *   --ink #0b0b12   --surface #15151f   --line  #ffffff14   --text  #f4f3f8
 *   --ink-2 #0f0f18 --surface-2 #1b1b27 --line-2 #ffffff24  --muted #9a99ad
 *   --brand #7c6cff --brand-2 #a855f7   --mint #34e2b0      --amber #f5a524
 *   --r 18px        --r-sm 12px
 *
 * Its fonts are system stacks (SF Pro Display / -apple-system), not webfonts,
 * so these pages load nothing extra.
 *
 * These pages are DARK regardless of the app's theme attribute: ThemeContext
 * pins data-theme="light" for the dashboard, and the tokens below are scoped to
 * .mp-auth so nothing here inherits or leaks.
 */

.mp-auth {
  /* Ground and surfaces */
  --ink: #0b0b12;
  --ink-2: #0f0f18;
  --surface: #15151f;
  --surface-2: #1b1b27;
  --line: #ffffff14;
  --line-2: #ffffff24;

  /* Type colour */
  --text: #f4f3f8;
  --muted: #9a99ad;
  --faint: #6c6b7e;

  /* Brand and status */
  --brand: #7c6cff;
  --brand-2: #a855f7;
  --mint: #34e2b0;
  --amber: #f5a524;
  --red: #ff5c5c;

  /* Shape */
  --r: 18px;
  --r-sm: 12px;

  /* Their stacks, verbatim */
  --display: 'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --body: 'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --mono: ui-monospace, "SF Mono", "JetBrains Mono", "Roboto Mono", monospace;

  position: relative;
  min-height: 100vh;
  padding: 40px 20px;
  background: var(--text);
  color: var(--text);
  font-family: var(--body);
  font-size: 15px;
  line-height: 1.6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
}

/* The site's signature top glow. Behind the card, never over it. */
.mp-auth::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 620px;
  /* background: radial-gradient(60% 100% at 50% 0, #7c6cff66, transparent 70%); */
  pointer-events: none;
}

.mp-auth ::selection { background: #7c6cff59; color: #fff; }

/* ─── Card ─────────────────────────────────────────────────────────────── */

.mp-auth-card {
  position: relative;
  width: 100%;
  /* The form column paints its own white; this only shows through the
     panel-less breakpoints and the rounded corners. */
  background: #ffffff;
  /* border: 1px solid #ffffff1f; */
  border-radius: var(--r);
  /* box-shadow: 0 40px 90px -50px #000, 0 0 0 1px #ffffff0f; */
  overflow: hidden;
}

/* Login and register have room for the supporting panel. */
.mp-auth-card.is-wide {
  max-width: 1000px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.82fr);
}

/* OTP and the password flows are single-purpose and stay focused. */
.mp-auth-card.is-narrow { max-width: 452px; }

/*
 * The form column is WHITE. It redeclares the token set as a light palette, so
 * every field, button and alert inside it picks up light values without any
 * component needing to know which side of the card it is on. The panel keeps
 * the dark values from .mp-auth.
 *
 * --muted and --faint are darkened from the site's dark-ground values
 * (#9a99ad / #6c6b7e): those are tuned for #0b0b12 and would sit at roughly
 * 2:1 on white, well under the 4.5:1 needed for body text.
 */
.mp-auth-body {
  --surface: #ffffff;
  --surface-2: #f1f1f7;
  --ink-2: #f8f8fc;
  --line: #ecebf3;
  --line-2: #dedbeb;
  --text: #14121f;
  --muted: #605e78;
  --faint: #8b899f;

  background: #ffffff;
  color: var(--text);
  padding: 40px 44px 38px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.mp-auth-card.is-narrow .mp-auth-body { padding: 38px 36px 34px; }

/* ─── Head ─────────────────────────────────────────────────────────────── */

.mp-auth-brand { margin-bottom: 34px; }
.mp-auth-brand img { height: 34px; width: auto; display: block; }

/* Icon tile for the single-purpose pages, in place of the logo lockup. */
.mp-auth-tile {
  width: 44px;
  height: 44px;
  margin-bottom: 20px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: #f1eeff;
  border: 1px solid #ddd6ff;
  color: var(--brand);
}

.mp-auth-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--iv-sans);
  font-size: 11.5px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--brand);
}

.mp-auth-title {
  margin: 14px 0 0;
  font-family: var(--display);
  font-size: 27px;
  font-weight: 700;
  line-height: 1.18;
  letter-spacing: -0.025em;
  color: var(--text);
  text-wrap: balance;
}
.mp-auth-card.is-narrow .mp-auth-title { font-size: 23px; }

.mp-auth-sub {
  margin: 10px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--muted);
  max-width: 42ch;
}
.mp-auth-sub strong { color: var(--text); font-weight: 600; }

/* ─── Tabs ─────────────────────────────────────────────────────────────── */

.mp-auth-tabs {
  display: flex;
  gap: 24px;
  margin: 26px 0 24px;
  border-bottom: 1px solid var(--line);
}
.mp-auth-tabs a,
.mp-auth-tabs span {
  position: relative;
  padding: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  color: var(--faint);
  transition: color 0.15s;
}
.mp-auth-tabs a:hover { color: var(--text); }
.mp-auth-tabs .is-active { color: var(--text); }
.mp-auth-tabs .is-active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: linear-gradient(120deg, var(--brand), var(--brand-2));
}

/* ─── Fields ───────────────────────────────────────────────────────────── */

.mp-auth-fields { display: flex; flex-direction: column; gap: 16px; }

.mp-auth-label {
  display: block;
  margin-bottom: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--muted);
  margin-top: 10px;
}

.mp-auth-field {
  display: flex;
  align-items: center;
  min-height: 46px;
  background: var(--ink-2);
  border: 1px solid var(--line-2);
  border-radius: var(--r-sm);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.mp-auth-field:focus-within {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px #7c6cff2e;
}
.mp-auth-field.has-error { border-color: var(--red); }
.mp-auth-field.has-error:focus-within { box-shadow: 0 0 0 3px #ff5c5c2e; }

.mp-auth-field input,
.mp-auth-field select {
  flex: 1 1;
  min-width: 0;
  height: 44px;
  padding: 0 14px;
  border: 0;
  background: transparent;
  font-family: var(--body);
  font-size: 14.5px;
  color: var(--text);
  outline: none;
}
.mp-auth-field input::placeholder { color: var(--faint); }
/* Chrome's autofill paints its own yellow ground; hold the dark surface. */
.mp-auth-field input:-webkit-autofill,
.mp-auth-field input:-webkit-autofill:focus {
  -webkit-text-fill-color: var(--text);
  -webkit-box-shadow: 0 0 0 100px var(--ink-2) inset;
  caret-color: var(--text);
  border-radius: var(--r-sm);
}

.mp-auth-eye {
  width: 42px;
  flex: 0 0 42px;
  align-self: stretch;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  color: var(--faint);
  cursor: pointer;
  border-radius: 0 var(--r-sm) var(--r-sm) 0;
}
.mp-auth-eye:hover { color: var(--muted); }

.mp-auth-err {
  display: block;
  margin-top: 7px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--red);
}
.mp-auth-hint {
  display: block;
  margin-top: 7px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--faint);
}

.mp-auth-forgot { margin: 16px 0 22px; text-align: right; }
.mp-auth-forgot a,
.mp-auth-link {
  font-size: 13px;
  font-weight: 600;
  color: var(--brand);
  text-decoration: none;
  background: none;
  border: 0;
  padding: 0;
  font-family: var(--body);
  cursor: pointer;
}
.mp-auth-forgot a:hover,
.mp-auth-link:hover { text-decoration: underline; }

/* ─── Alerts ───────────────────────────────────────────────────────────── */

.mp-auth-alert {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-bottom: 18px;
  padding: 11px 13px;
  border-radius: var(--r-sm);
  font-size: 13.5px;
  line-height: 1.5;
}
.mp-auth-alert svg { flex: 0 0 auto; margin-top: 1px; }
/* Tuned for the white column: the dark-ground tints were near-invisible and
   the pale text would have failed contrast. */
.mp-auth-alert.is-err {
  background: #fdeef0;
  border: 1px solid #f7ccd3;
  color: #b3243f;
}
.mp-auth-alert.is-ok {
  background: #e8f8f2;
  border: 1px solid #bfe9d9;
  color: #0a7a58;
}

/* ─── Buttons (the site's .btn / .btn-primary) ─────────────────────────── */

.mp-auth-btn {
  height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 22px;
  border: 1px solid var(--line-2);
  border-radius: 999px;
  background: transparent;
  color: var(--text);
  font-family: var(--body);
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s, background 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.mp-auth-btn:hover:not(:disabled) { background: #f6f5fb; border-color: #c9c4de; color: #000000;}

.mp-auth-btn.is-primary {
  border: none;
  /* Dark ink on the gradient is the site's own treatment, and it clears
     contrast far more comfortably than white would on #a855f7. */
  color: #ffffff;
  font-weight: 700;
  background: linear-gradient(120deg, var(--brand), var(--brand-2));
  box-shadow: 0 8px 30px -10px #7c6cffb3;
}
.mp-auth-btn.is-primary:hover:not(:disabled) { transform: translateY(-1px); }
.mp-auth-btn.is-primary:active:not(:disabled) { transform: none; }

.mp-auth-btn.is-block { width: 100%; margin-top: 20px;}
.mp-auth-btn:disabled { opacity: 0.55; cursor: default; transform: none; }

.mp-auth-spin {
  width: 17px;
  height: 17px;
  border: 2px solid #0c0a1640;
  border-top-color: #0c0a16;
  border-radius: 50%;
  animation: mp-auth-spin 0.7s linear infinite;
}
.mp-auth-btn:not(.is-primary) .mp-auth-spin {
  border-color: #14121f33;
  border-top-color: var(--text);
}
@keyframes mp-auth-spin { to { transform: rotate(360deg); } }

.mp-auth-or {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 24px 0 16px;
  font-size: 12.5px;
  color: var(--faint);
}
.mp-auth-or::before,
.mp-auth-or::after { content: ''; flex: 1 1; height: 1px; background: var(--line); }

/* GoogleSignInButton positions its real, transparent button against
   .rd-social-btn-wrap with inset:0 — that class has to stay. */
.mp-auth .rd-social-btn-wrap { position: relative; }

.mp-auth-foot {
  margin: 24px 0 0;
  font-size: 13px;
  color: var(--muted);
  text-align: center;
}
.mp-auth-foot a { color: var(--brand); font-weight: 600; text-decoration: none; }
.mp-auth-foot a:hover { text-decoration: underline; }

/* Row with a back link on the left and the action on the right. */
.mp-auth-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--line);
}
.mp-auth-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 0;
  font-family: var(--body);
  font-size: 14px;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
}
.mp-auth-back:hover { color: var(--text); }

/* ─── Register: consent rows ───────────────────────────────────────────── */

.mp-auth-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
}
.mp-auth-consents {
  display: flex;
  flex-direction: column;
  gap: 11px;
  margin: 20px 0 6px;
}
.mp-auth-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--muted);
  cursor: pointer;
}
.mp-auth-check input[type='checkbox'] {
  flex: 0 0 auto;
  width: 15px;
  height: 15px;
  margin: 1px 0 0;
  accent-color: var(--brand);
  cursor: pointer;
}
.mp-auth-check a { color: var(--brand); font-weight: 600; text-decoration: none; }
.mp-auth-check a:hover { text-decoration: underline; }

/* ─── OTP boxes (OTPInput renders .rd-otp-group / .rd-otp-box) ─────────── */

.mp-auth .rd-otp-group {
  display: flex;
  gap: 9px;
  justify-content: space-between;
  margin: 15px 0 2px;
}
.mp-auth .rd-otp-box {
  flex: 1 1;
  min-width: 0;
  aspect-ratio: 1 / 1;
  max-width: 56px;
  text-align: center;
  font-family: var(--mono);
  font-size: 21px;
  font-weight: 600;
  color: var(--text);
  background: var(--ink-2);
  border: 1px solid var(--line-2);
  border-radius: var(--r-sm);
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.mp-auth .rd-otp-box:focus {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px #7c6cff2e;
}

.mp-auth-resend {
  margin: 18px 0 0;
  font-size: 13px;
  color: var(--faint);
  text-align: center;
}
.mp-auth-timer { font-family: var(--mono); color: var(--brand); font-weight: 600; }

/* Trust-this-device row. */
.mp-auth-trust {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 18px 0 0;
  padding: 12px 14px;
  background: var(--ink-2);
  border: 1px solid var(--line);
  border-radius: var(--r-sm);
  font-size: 13px;
  color: var(--muted);
  cursor: pointer;
}
.mp-auth-trust input[type='checkbox'] {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  accent-color: var(--brand);
  cursor: pointer;
}
.mp-auth-trust select {
  height: 34px;
  padding: 0 8px;
  background: var(--surface-2);
  border: 1px solid var(--line-2);
  border-radius: 9px;
  color: var(--text);
  font-family: var(--body);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

/* ─── Statement panel (wide only) ──────────────────────────────────────────
   Deliberately almost empty: a headline, one line, one trust signal. It exists
   to make the page feel like a product rather than a form, and the weight comes
   from type scale and the brand glow, not from copy.
   ═════════════════════════════════════════════════════════════════════════ */

.mp-auth-panel {
  position: relative;
  overflow: hidden;
  padding: 52px 44px 44px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: var(--ink-2);
  color: var(--text);
}

/* Two offset orbs. The composition is the eye-catching part, so it gets to be
   generous; there is no content underneath it to compete with. */
.mp-auth-panel::before,
.mp-auth-panel::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}
.mp-auth-panel::before {
  width: 420px;
  height: 420px;
  top: -140px;
  right: -130px;
  background: radial-gradient(50% 50%, #7c6cff8c, #a855f72e 45%, transparent 70%);
  filter: blur(4px);
}
.mp-auth-panel::after {
  width: 320px;
  height: 320px;
  bottom: -120px;
  left: -100px;
  background: radial-gradient(50% 50%, #a855f759, #7c6cff1f 48%, transparent 72%);
}

/* Hairline grid, barely there. A ledger surface rather than decoration. */
.mp-auth-panel-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(#ffffff0a 1px, transparent 1px),
    linear-gradient(90deg, #ffffff0a 1px, transparent 1px);
  background-size: 46px 46px;
  mask-image: radial-gradient(80% 70% at 50% 40%, #000, transparent 75%);
  -webkit-mask-image: radial-gradient(80% 70% at 50% 40%, #000, transparent 75%);
  pointer-events: none;
}

.mp-auth-panel-inner { position: relative; }

.mp-auth-panel h2 {
  margin: 18px 0 0;
  font-family: var(--display);
  font-size: 40px;
  font-weight: 700;
  line-height: 1.06;
  letter-spacing: -0.04em;
  color: #ffffff;
  text-wrap: balance;
}
.mp-auth-panel h2 em {
  font-style: normal;
  background: linear-gradient(120deg, #cfc7ff, #e9c8ff);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.mp-auth-panel > .mp-auth-panel-inner > p {
  margin: 16px 0 0;
  font-size: 14.5px;
  line-height: 1.62;
  color: #c3c1d6;
  max-width: 30ch;
}

.mp-auth-panel-note {
  position: relative;
  margin: 40px 0 0;
  padding-top: 20px;
  border-top: 1px solid #ffffff1f;
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 12px;
  color: #a9a7c0;
}
.mp-auth-panel-note svg { flex: 0 0 auto; color: var(--mint); }

/* ─── Responsive ───────────────────────────────────────────────────────── */

@media (max-width: 979px) {
  .mp-auth-card.is-wide { grid-template-columns: minmax(0, 1fr); max-width: 452px; }
  .mp-auth-panel { display: none; }
  .mp-auth-body { padding: 36px 30px 32px; }
}

@media (max-width: 520px) {
  .mp-auth { padding: 0; align-items: stretch; }
  .mp-auth-card,
  .mp-auth-card.is-wide,
  .mp-auth-card.is-narrow {
    max-width: none;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    min-height: 100vh;
    align-content: center;
  }
  .mp-auth-body { padding: 30px 20px; }
  .mp-auth-title { font-size: 22px; }
  .mp-auth-row { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  .mp-auth .rd-otp-group { gap: 7px; }
  .mp-auth .rd-otp-box { font-size: 18px; }
}

@media (prefers-reduced-motion: reduce) {
  .mp-auth-btn,
  .mp-auth-field,
  .mp-auth-tabs a { transition: none; }
  .mp-auth-btn.is-primary:hover:not(:disabled) { transform: none; }
  .mp-auth-spin { animation-duration: 1.6s; }
}
`, "",{"version":3,"sources":["webpack://./src/components/auth/AuthShell.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;;;;;;;EAoBE;;AAEF;EACE,wBAAwB;EACxB,cAAc;EACd,gBAAgB;EAChB,kBAAkB;EAClB,oBAAoB;EACpB,iBAAiB;EACjB,mBAAmB;;EAEnB,gBAAgB;EAChB,eAAe;EACf,gBAAgB;EAChB,gBAAgB;;EAEhB,qBAAqB;EACrB,gBAAgB;EAChB,kBAAkB;EAClB,eAAe;EACf,gBAAgB;EAChB,cAAc;;EAEd,UAAU;EACV,SAAS;EACT,YAAY;;EAEZ,2BAA2B;EAC3B,8EAA8E;EAC9E,2EAA2E;EAC3E,2EAA2E;;EAE3E,kBAAkB;EAClB,iBAAiB;EACjB,kBAAkB;EAClB,uBAAuB;EACvB,kBAAkB;EAClB,wBAAwB;EACxB,eAAe;EACf,gBAAgB;EAChB,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,kBAAkB;AACpB;;AAEA,mEAAmE;AACnE;EACE,WAAW;EACX,kBAAkB;EAClB,eAAe;EACf,aAAa;EACb,gFAAgF;EAChF,oBAAoB;AACtB;;AAEA,uBAAuB,qBAAqB,EAAE,WAAW,EAAE;;AAE3D,6EAA6E;;AAE7E;EACE,kBAAkB;EAClB,WAAW;EACX;sDACoD;EACpD,mBAAmB;EACnB,iCAAiC;EACjC,uBAAuB;EACvB,6DAA6D;EAC7D,gBAAgB;AAClB;;AAEA,2DAA2D;AAC3D;EACE,iBAAiB;EACjB,aAAa;EACb,uDAAuD;AACzD;;AAEA,oEAAoE;AACpE,0BAA0B,gBAAgB,EAAE;;AAE5C;;;;;;;;;EASE;AACF;EACE,kBAAkB;EAClB,oBAAoB;EACpB,gBAAgB;EAChB,eAAe;EACf,iBAAiB;EACjB,eAAe;EACf,gBAAgB;EAChB,gBAAgB;;EAEhB,mBAAmB;EACnB,kBAAkB;EAClB,uBAAuB;EACvB,aAAa;EACb,sBAAsB;EACtB,YAAY;AACd;AACA,wCAAwC,uBAAuB,EAAE;;AAEjE,6EAA6E;;AAE7E,iBAAiB,mBAAmB,EAAE;AACtC,qBAAqB,YAAY,EAAE,WAAW,EAAE,cAAc,EAAE;;AAEhE,yEAAyE;AACzE;EACE,WAAW;EACX,YAAY;EACZ,mBAAmB;EACnB,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,mBAAmB;EACnB,yBAAyB;EACzB,mBAAmB;AACrB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,2BAA2B;EAC3B,iBAAiB;EACjB,sBAAsB;EACtB,yBAAyB;EACzB,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;EAChB,2BAA2B;EAC3B,eAAe;EACf,gBAAgB;EAChB,iBAAiB;EACjB,wBAAwB;EACxB,kBAAkB;EAClB,kBAAkB;AACpB;AACA,yCAAyC,eAAe,EAAE;;AAE1D;EACE,gBAAgB;EAChB,eAAe;EACf,gBAAgB;EAChB,mBAAmB;EACnB,eAAe;AACjB;AACA,sBAAsB,kBAAkB,EAAE,gBAAgB,EAAE;;AAE5D,6EAA6E;;AAE7E;EACE,aAAa;EACb,SAAS;EACT,mBAAmB;EACnB,oCAAoC;AACtC;AACA;;EAEE,kBAAkB;EAClB,iBAAiB;EACjB,eAAe;EACf,gBAAgB;EAChB,qBAAqB;EACrB,mBAAmB;EACnB,uBAAuB;AACzB;AACA,wBAAwB,kBAAkB,EAAE;AAC5C,2BAA2B,kBAAkB,EAAE;AAC/C;EACE,WAAW;EACX,kBAAkB;EAClB,OAAO;EACP,QAAQ;EACR,YAAY;EACZ,WAAW;EACX,0BAA0B;EAC1B,iEAAiE;AACnE;;AAEA,6EAA6E;;AAE7E,kBAAkB,aAAa,EAAE,sBAAsB,EAAE,SAAS,EAAE;;AAEpE;EACE,cAAc;EACd,kBAAkB;EAClB,iBAAiB;EACjB,gBAAgB;EAChB,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,gBAAgB;EAChB,wBAAwB;EACxB,+BAA+B;EAC/B,0BAA0B;EAC1B,gDAAgD;AAClD;AACA;EACE,0BAA0B;EAC1B,+BAA+B;AACjC;AACA,2BAA2B,wBAAwB,EAAE;AACrD,wCAAwC,+BAA+B,EAAE;;AAEzE;;EAEE,SAAO;EACP,YAAY;EACZ,YAAY;EACZ,eAAe;EACf,SAAS;EACT,uBAAuB;EACvB,wBAAwB;EACxB,iBAAiB;EACjB,kBAAkB;EAClB,aAAa;AACf;AACA,oCAAoC,mBAAmB,EAAE;AACzD,2EAA2E;AAC3E;;EAEE,oCAAoC;EACpC,kDAAkD;EAClD,wBAAwB;EACxB,0BAA0B;AAC5B;;AAEA;EACE,WAAW;EACX,cAAc;EACd,mBAAmB;EACnB,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,uBAAuB;EACvB,mBAAmB;EACnB,eAAe;EACf,0CAA0C;AAC5C;AACA,qBAAqB,mBAAmB,EAAE;;AAE1C;EACE,cAAc;EACd,eAAe;EACf,iBAAiB;EACjB,gBAAgB;EAChB,iBAAiB;AACnB;AACA;EACE,cAAc;EACd,eAAe;EACf,eAAe;EACf,gBAAgB;EAChB,mBAAmB;AACrB;;AAEA,kBAAkB,mBAAmB,EAAE,iBAAiB,EAAE;AAC1D;;EAEE,eAAe;EACf,gBAAgB;EAChB,mBAAmB;EACnB,qBAAqB;EACrB,gBAAgB;EAChB,SAAS;EACT,UAAU;EACV,wBAAwB;EACxB,eAAe;AACjB;AACA;sBACsB,0BAA0B,EAAE;;AAElD,6EAA6E;;AAE7E;EACE,aAAa;EACb,uBAAuB;EACvB,QAAQ;EACR,mBAAmB;EACnB,kBAAkB;EAClB,0BAA0B;EAC1B,iBAAiB;EACjB,gBAAgB;AAClB;AACA,qBAAqB,cAAc,EAAE,eAAe,EAAE;AACtD;8CAC8C;AAC9C;EACE,mBAAmB;EACnB,yBAAyB;EACzB,cAAc;AAChB;AACA;EACE,mBAAmB;EACnB,yBAAyB;EACzB,cAAc;AAChB;;AAEA,6EAA6E;;AAE7E;EACE,YAAY;EACZ,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,eAAe;EACf,+BAA+B;EAC/B,oBAAoB;EACpB,uBAAuB;EACvB,kBAAkB;EAClB,wBAAwB;EACxB,iBAAiB;EACjB,gBAAgB;EAChB,eAAe;EACf,mFAAmF;AACrF;AACA,oCAAoC,mBAAmB,EAAE,qBAAqB,EAAE,cAAc,CAAC;;AAE/F;EACE,YAAY;EACZ;iEAC+D;EAC/D,cAAc;EACd,gBAAgB;EAChB,iEAAiE;EACjE,sCAAsC;AACxC;AACA,+CAA+C,2BAA2B,EAAE;AAC5E,gDAAgD,eAAe,EAAE;;AAEjE,wBAAwB,WAAW,EAAE,gBAAgB,CAAC;AACtD,wBAAwB,aAAa,EAAE,eAAe,EAAE,eAAe,EAAE;;AAEzE;EACE,WAAW;EACX,YAAY;EACZ,2BAA2B;EAC3B,yBAAyB;EACzB,kBAAkB;EAClB,4CAA4C;AAC9C;AACA;EACE,uBAAuB;EACvB,6BAA6B;AAC/B;AACA,0BAA0B,KAAK,yBAAyB,EAAE,EAAE;;AAE5D;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,mBAAmB;EACnB,iBAAiB;EACjB,mBAAmB;AACrB;AACA;qBACqB,WAAW,EAAE,SAAO,EAAE,WAAW,EAAE,uBAAuB,EAAE;;AAEjF;+DAC+D;AAC/D,+BAA+B,kBAAkB,EAAE;;AAEnD;EACE,gBAAgB;EAChB,eAAe;EACf,mBAAmB;EACnB,kBAAkB;AACpB;AACA,kBAAkB,mBAAmB,EAAE,gBAAgB,EAAE,qBAAqB,EAAE;AAChF,wBAAwB,0BAA0B,EAAE;;AAEpD,kEAAkE;AAClE;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,SAAS;EACT,gBAAgB;EAChB,iBAAiB;EACjB,iCAAiC;AACnC;AACA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,SAAS;EACT,gBAAgB;EAChB,UAAU;EACV,wBAAwB;EACxB,eAAe;EACf,gBAAgB;EAChB,mBAAmB;EACnB,eAAe;AACjB;AACA,sBAAsB,kBAAkB,EAAE;;AAE1C,6EAA6E;;AAE7E;EACE,aAAa;EACb,oDAAoD;EACpD,SAAS;AACX;AACA;EACE,aAAa;EACb,sBAAsB;EACtB,SAAS;EACT,kBAAkB;AACpB;AACA;EACE,aAAa;EACb,uBAAuB;EACvB,SAAS;EACT,iBAAiB;EACjB,gBAAgB;EAChB,mBAAmB;EACnB,eAAe;AACjB;AACA;EACE,cAAc;EACd,WAAW;EACX,YAAY;EACZ,eAAe;EACf,0BAA0B;EAC1B,eAAe;AACjB;AACA,mBAAmB,mBAAmB,EAAE,gBAAgB,EAAE,qBAAqB,EAAE;AACjF,yBAAyB,0BAA0B,EAAE;;AAErD,6EAA6E;;AAE7E;EACE,aAAa;EACb,QAAQ;EACR,8BAA8B;EAC9B,kBAAkB;AACpB;AACA;EACE,SAAO;EACP,YAAY;EACZ,mBAAmB;EACnB,eAAe;EACf,kBAAkB;EAClB,wBAAwB;EACxB,eAAe;EACf,gBAAgB;EAChB,kBAAkB;EAClB,wBAAwB;EACxB,+BAA+B;EAC/B,0BAA0B;EAC1B,aAAa;EACb,gDAAgD;AAClD;AACA;EACE,0BAA0B;EAC1B,+BAA+B;AACjC;;AAEA;EACE,gBAAgB;EAChB,eAAe;EACf,mBAAmB;EACnB,kBAAkB;AACpB;AACA,iBAAiB,wBAAwB,EAAE,mBAAmB,EAAE,gBAAgB,EAAE;;AAElF,2BAA2B;AAC3B;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,eAAe;EACf,gBAAgB;EAChB,kBAAkB;EAClB,wBAAwB;EACxB,6BAA6B;EAC7B,0BAA0B;EAC1B,eAAe;EACf,mBAAmB;EACnB,eAAe;AACjB;AACA;EACE,WAAW;EACX,YAAY;EACZ,cAAc;EACd,0BAA0B;EAC1B,eAAe;AACjB;AACA;EACE,YAAY;EACZ,cAAc;EACd,4BAA4B;EAC5B,+BAA+B;EAC/B,kBAAkB;EAClB,kBAAkB;EAClB,wBAAwB;EACxB,iBAAiB;EACjB,gBAAgB;EAChB,eAAe;AACjB;;AAEA;;;;8EAI8E;;AAE9E;EACE,kBAAkB;EAClB,gBAAgB;EAChB,uBAAuB;EACvB,aAAa;EACb,sBAAsB;EACtB,uBAAuB;EACvB,wBAAwB;EACxB,kBAAkB;AACpB;;AAEA;iEACiE;AACjE;;EAEE,WAAW;EACX,kBAAkB;EAClB,kBAAkB;EAClB,oBAAoB;AACtB;AACA;EACE,YAAY;EACZ,aAAa;EACb,WAAW;EACX,aAAa;EACb,+EAA+E;EAC/E,iBAAiB;AACnB;AACA;EACE,YAAY;EACZ,aAAa;EACb,cAAc;EACd,YAAY;EACZ,+EAA+E;AACjF;;AAEA,0EAA0E;AAC1E;EACE,kBAAkB;EAClB,QAAQ;EACR;;0DAEwD;EACxD,0BAA0B;EAC1B,sEAAsE;EACtE,8EAA8E;EAC9E,oBAAoB;AACtB;;AAEA,uBAAuB,kBAAkB,EAAE;;AAE3C;EACE,gBAAgB;EAChB,2BAA2B;EAC3B,eAAe;EACf,gBAAgB;EAChB,iBAAiB;EACjB,uBAAuB;EACvB,cAAc;EACd,kBAAkB;AACpB;AACA;EACE,kBAAkB;EAClB,qDAAqD;EACrD,6BAA6B;EAC7B,qBAAqB;EACrB,kBAAkB;AACpB;;AAEA;EACE,gBAAgB;EAChB,iBAAiB;EACjB,iBAAiB;EACjB,cAAc;EACd,eAAe;AACjB;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,iBAAiB;EACjB,+BAA+B;EAC/B,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,eAAe;EACf,cAAc;AAChB;AACA,0BAA0B,cAAc,EAAE,kBAAkB,EAAE;;AAE9D,6EAA6E;;AAE7E;EACE,wBAAwB,qCAAqC,EAAE,gBAAgB,EAAE;EACjF,iBAAiB,aAAa,EAAE;EAChC,gBAAgB,uBAAuB,EAAE;AAC3C;;AAEA;EACE,WAAW,UAAU,EAAE,oBAAoB,EAAE;EAC7C;;;IAGE,eAAe;IACf,SAAS;IACT,gBAAgB;IAChB,gBAAgB;IAChB,iBAAiB;IACjB,qBAAqB;EACvB;EACA,gBAAgB,kBAAkB,EAAE;EACpC,iBAAiB,eAAe,EAAE;EAClC,eAAe,qCAAqC,EAAE,SAAS,EAAE;EACjE,yBAAyB,QAAQ,EAAE;EACnC,uBAAuB,eAAe,EAAE;AAC1C;;AAEA;EACE;;oBAEkB,gBAAgB,EAAE;EACpC,+CAA+C,eAAe,EAAE;EAChE,gBAAgB,wBAAwB,EAAE;AAC5C","sourcesContent":["/*\n * AuthShell.css — sign-in, sign-up, OTP and password-reset pages.\n *\n * The design system here is lifted from the marketing site at\n * dev-new.maxelpay.com, whose /login link points at this very app\n * (dev-dashboard.maxelpay.com/login). Someone arriving from the site should not\n * notice a change of product, so the tokens below are its values verbatim,\n * read from its own stylesheets rather than eyeballed:\n *\n *   --ink #0b0b12   --surface #15151f   --line  #ffffff14   --text  #f4f3f8\n *   --ink-2 #0f0f18 --surface-2 #1b1b27 --line-2 #ffffff24  --muted #9a99ad\n *   --brand #7c6cff --brand-2 #a855f7   --mint #34e2b0      --amber #f5a524\n *   --r 18px        --r-sm 12px\n *\n * Its fonts are system stacks (SF Pro Display / -apple-system), not webfonts,\n * so these pages load nothing extra.\n *\n * These pages are DARK regardless of the app's theme attribute: ThemeContext\n * pins data-theme=\"light\" for the dashboard, and the tokens below are scoped to\n * .mp-auth so nothing here inherits or leaks.\n */\n\n.mp-auth {\n  /* Ground and surfaces */\n  --ink: #0b0b12;\n  --ink-2: #0f0f18;\n  --surface: #15151f;\n  --surface-2: #1b1b27;\n  --line: #ffffff14;\n  --line-2: #ffffff24;\n\n  /* Type colour */\n  --text: #f4f3f8;\n  --muted: #9a99ad;\n  --faint: #6c6b7e;\n\n  /* Brand and status */\n  --brand: #7c6cff;\n  --brand-2: #a855f7;\n  --mint: #34e2b0;\n  --amber: #f5a524;\n  --red: #ff5c5c;\n\n  /* Shape */\n  --r: 18px;\n  --r-sm: 12px;\n\n  /* Their stacks, verbatim */\n  --display: 'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;\n  --body: 'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;\n  --mono: ui-monospace, \"SF Mono\", \"JetBrains Mono\", \"Roboto Mono\", monospace;\n\n  position: relative;\n  min-height: 100vh;\n  padding: 40px 20px;\n  background: var(--text);\n  color: var(--text);\n  font-family: var(--body);\n  font-size: 15px;\n  line-height: 1.6;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  overflow-x: hidden;\n}\n\n/* The site's signature top glow. Behind the card, never over it. */\n.mp-auth::before {\n  content: '';\n  position: absolute;\n  inset: 0 0 auto;\n  height: 620px;\n  /* background: radial-gradient(60% 100% at 50% 0, #7c6cff66, transparent 70%); */\n  pointer-events: none;\n}\n\n.mp-auth ::selection { background: #7c6cff59; color: #fff; }\n\n/* ─── Card ─────────────────────────────────────────────────────────────── */\n\n.mp-auth-card {\n  position: relative;\n  width: 100%;\n  /* The form column paints its own white; this only shows through the\n     panel-less breakpoints and the rounded corners. */\n  background: #ffffff;\n  /* border: 1px solid #ffffff1f; */\n  border-radius: var(--r);\n  /* box-shadow: 0 40px 90px -50px #000, 0 0 0 1px #ffffff0f; */\n  overflow: hidden;\n}\n\n/* Login and register have room for the supporting panel. */\n.mp-auth-card.is-wide {\n  max-width: 1000px;\n  display: grid;\n  grid-template-columns: minmax(0, 1fr) minmax(0, 0.82fr);\n}\n\n/* OTP and the password flows are single-purpose and stay focused. */\n.mp-auth-card.is-narrow { max-width: 452px; }\n\n/*\n * The form column is WHITE. It redeclares the token set as a light palette, so\n * every field, button and alert inside it picks up light values without any\n * component needing to know which side of the card it is on. The panel keeps\n * the dark values from .mp-auth.\n *\n * --muted and --faint are darkened from the site's dark-ground values\n * (#9a99ad / #6c6b7e): those are tuned for #0b0b12 and would sit at roughly\n * 2:1 on white, well under the 4.5:1 needed for body text.\n */\n.mp-auth-body {\n  --surface: #ffffff;\n  --surface-2: #f1f1f7;\n  --ink-2: #f8f8fc;\n  --line: #ecebf3;\n  --line-2: #dedbeb;\n  --text: #14121f;\n  --muted: #605e78;\n  --faint: #8b899f;\n\n  background: #ffffff;\n  color: var(--text);\n  padding: 40px 44px 38px;\n  display: flex;\n  flex-direction: column;\n  min-width: 0;\n}\n.mp-auth-card.is-narrow .mp-auth-body { padding: 38px 36px 34px; }\n\n/* ─── Head ─────────────────────────────────────────────────────────────── */\n\n.mp-auth-brand { margin-bottom: 34px; }\n.mp-auth-brand img { height: 34px; width: auto; display: block; }\n\n/* Icon tile for the single-purpose pages, in place of the logo lockup. */\n.mp-auth-tile {\n  width: 44px;\n  height: 44px;\n  margin-bottom: 20px;\n  display: grid;\n  place-items: center;\n  border-radius: 14px;\n  background: #f1eeff;\n  border: 1px solid #ddd6ff;\n  color: var(--brand);\n}\n\n.mp-auth-eyebrow {\n  display: inline-flex;\n  align-items: center;\n  gap: 8px;\n  font-family: var(--iv-sans);\n  font-size: 11.5px;\n  letter-spacing: 0.16em;\n  text-transform: uppercase;\n  color: var(--brand);\n}\n\n.mp-auth-title {\n  margin: 14px 0 0;\n  font-family: var(--display);\n  font-size: 27px;\n  font-weight: 700;\n  line-height: 1.18;\n  letter-spacing: -0.025em;\n  color: var(--text);\n  text-wrap: balance;\n}\n.mp-auth-card.is-narrow .mp-auth-title { font-size: 23px; }\n\n.mp-auth-sub {\n  margin: 10px 0 0;\n  font-size: 14px;\n  line-height: 1.6;\n  color: var(--muted);\n  max-width: 42ch;\n}\n.mp-auth-sub strong { color: var(--text); font-weight: 600; }\n\n/* ─── Tabs ─────────────────────────────────────────────────────────────── */\n\n.mp-auth-tabs {\n  display: flex;\n  gap: 24px;\n  margin: 26px 0 24px;\n  border-bottom: 1px solid var(--line);\n}\n.mp-auth-tabs a,\n.mp-auth-tabs span {\n  position: relative;\n  padding: 0 0 12px;\n  font-size: 14px;\n  font-weight: 600;\n  text-decoration: none;\n  color: var(--faint);\n  transition: color 0.15s;\n}\n.mp-auth-tabs a:hover { color: var(--text); }\n.mp-auth-tabs .is-active { color: var(--text); }\n.mp-auth-tabs .is-active::after {\n  content: '';\n  position: absolute;\n  left: 0;\n  right: 0;\n  bottom: -1px;\n  height: 2px;\n  border-radius: 2px 2px 0 0;\n  background: linear-gradient(120deg, var(--brand), var(--brand-2));\n}\n\n/* ─── Fields ───────────────────────────────────────────────────────────── */\n\n.mp-auth-fields { display: flex; flex-direction: column; gap: 16px; }\n\n.mp-auth-label {\n  display: block;\n  margin-bottom: 7px;\n  font-size: 12.5px;\n  font-weight: 600;\n  color: var(--muted);\n  margin-top: 10px;\n}\n\n.mp-auth-field {\n  display: flex;\n  align-items: center;\n  min-height: 46px;\n  background: var(--ink-2);\n  border: 1px solid var(--line-2);\n  border-radius: var(--r-sm);\n  transition: border-color 0.15s, box-shadow 0.15s;\n}\n.mp-auth-field:focus-within {\n  border-color: var(--brand);\n  box-shadow: 0 0 0 3px #7c6cff2e;\n}\n.mp-auth-field.has-error { border-color: var(--red); }\n.mp-auth-field.has-error:focus-within { box-shadow: 0 0 0 3px #ff5c5c2e; }\n\n.mp-auth-field input,\n.mp-auth-field select {\n  flex: 1;\n  min-width: 0;\n  height: 44px;\n  padding: 0 14px;\n  border: 0;\n  background: transparent;\n  font-family: var(--body);\n  font-size: 14.5px;\n  color: var(--text);\n  outline: none;\n}\n.mp-auth-field input::placeholder { color: var(--faint); }\n/* Chrome's autofill paints its own yellow ground; hold the dark surface. */\n.mp-auth-field input:-webkit-autofill,\n.mp-auth-field input:-webkit-autofill:focus {\n  -webkit-text-fill-color: var(--text);\n  -webkit-box-shadow: 0 0 0 100px var(--ink-2) inset;\n  caret-color: var(--text);\n  border-radius: var(--r-sm);\n}\n\n.mp-auth-eye {\n  width: 42px;\n  flex: 0 0 42px;\n  align-self: stretch;\n  display: grid;\n  place-items: center;\n  border: 0;\n  background: transparent;\n  color: var(--faint);\n  cursor: pointer;\n  border-radius: 0 var(--r-sm) var(--r-sm) 0;\n}\n.mp-auth-eye:hover { color: var(--muted); }\n\n.mp-auth-err {\n  display: block;\n  margin-top: 7px;\n  font-size: 12.5px;\n  font-weight: 500;\n  color: var(--red);\n}\n.mp-auth-hint {\n  display: block;\n  margin-top: 7px;\n  font-size: 12px;\n  line-height: 1.5;\n  color: var(--faint);\n}\n\n.mp-auth-forgot { margin: 16px 0 22px; text-align: right; }\n.mp-auth-forgot a,\n.mp-auth-link {\n  font-size: 13px;\n  font-weight: 600;\n  color: var(--brand);\n  text-decoration: none;\n  background: none;\n  border: 0;\n  padding: 0;\n  font-family: var(--body);\n  cursor: pointer;\n}\n.mp-auth-forgot a:hover,\n.mp-auth-link:hover { text-decoration: underline; }\n\n/* ─── Alerts ───────────────────────────────────────────────────────────── */\n\n.mp-auth-alert {\n  display: flex;\n  align-items: flex-start;\n  gap: 9px;\n  margin-bottom: 18px;\n  padding: 11px 13px;\n  border-radius: var(--r-sm);\n  font-size: 13.5px;\n  line-height: 1.5;\n}\n.mp-auth-alert svg { flex: 0 0 auto; margin-top: 1px; }\n/* Tuned for the white column: the dark-ground tints were near-invisible and\n   the pale text would have failed contrast. */\n.mp-auth-alert.is-err {\n  background: #fdeef0;\n  border: 1px solid #f7ccd3;\n  color: #b3243f;\n}\n.mp-auth-alert.is-ok {\n  background: #e8f8f2;\n  border: 1px solid #bfe9d9;\n  color: #0a7a58;\n}\n\n/* ─── Buttons (the site's .btn / .btn-primary) ─────────────────────────── */\n\n.mp-auth-btn {\n  height: 46px;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 8px;\n  padding: 0 22px;\n  border: 1px solid var(--line-2);\n  border-radius: 999px;\n  background: transparent;\n  color: var(--text);\n  font-family: var(--body);\n  font-size: 14.5px;\n  font-weight: 600;\n  cursor: pointer;\n  transition: transform 0.15s, background 0.15s, border-color 0.15s, box-shadow 0.15s;\n}\n.mp-auth-btn:hover:not(:disabled) { background: #f6f5fb; border-color: #c9c4de; color: #000000;}\n\n.mp-auth-btn.is-primary {\n  border: none;\n  /* Dark ink on the gradient is the site's own treatment, and it clears\n     contrast far more comfortably than white would on #a855f7. */\n  color: #ffffff;\n  font-weight: 700;\n  background: linear-gradient(120deg, var(--brand), var(--brand-2));\n  box-shadow: 0 8px 30px -10px #7c6cffb3;\n}\n.mp-auth-btn.is-primary:hover:not(:disabled) { transform: translateY(-1px); }\n.mp-auth-btn.is-primary:active:not(:disabled) { transform: none; }\n\n.mp-auth-btn.is-block { width: 100%; margin-top: 20px;}\n.mp-auth-btn:disabled { opacity: 0.55; cursor: default; transform: none; }\n\n.mp-auth-spin {\n  width: 17px;\n  height: 17px;\n  border: 2px solid #0c0a1640;\n  border-top-color: #0c0a16;\n  border-radius: 50%;\n  animation: mp-auth-spin 0.7s linear infinite;\n}\n.mp-auth-btn:not(.is-primary) .mp-auth-spin {\n  border-color: #14121f33;\n  border-top-color: var(--text);\n}\n@keyframes mp-auth-spin { to { transform: rotate(360deg); } }\n\n.mp-auth-or {\n  display: flex;\n  align-items: center;\n  gap: 14px;\n  margin: 24px 0 16px;\n  font-size: 12.5px;\n  color: var(--faint);\n}\n.mp-auth-or::before,\n.mp-auth-or::after { content: ''; flex: 1; height: 1px; background: var(--line); }\n\n/* GoogleSignInButton positions its real, transparent button against\n   .rd-social-btn-wrap with inset:0 — that class has to stay. */\n.mp-auth .rd-social-btn-wrap { position: relative; }\n\n.mp-auth-foot {\n  margin: 24px 0 0;\n  font-size: 13px;\n  color: var(--muted);\n  text-align: center;\n}\n.mp-auth-foot a { color: var(--brand); font-weight: 600; text-decoration: none; }\n.mp-auth-foot a:hover { text-decoration: underline; }\n\n/* Row with a back link on the left and the action on the right. */\n.mp-auth-actions {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 12px;\n  margin-top: 24px;\n  padding-top: 20px;\n  border-top: 1px solid var(--line);\n}\n.mp-auth-back {\n  display: inline-flex;\n  align-items: center;\n  gap: 6px;\n  border: 0;\n  background: none;\n  padding: 0;\n  font-family: var(--body);\n  font-size: 14px;\n  font-weight: 600;\n  color: var(--muted);\n  cursor: pointer;\n}\n.mp-auth-back:hover { color: var(--text); }\n\n/* ─── Register: consent rows ───────────────────────────────────────────── */\n\n.mp-auth-row {\n  display: grid;\n  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);\n  gap: 14px;\n}\n.mp-auth-consents {\n  display: flex;\n  flex-direction: column;\n  gap: 11px;\n  margin: 20px 0 6px;\n}\n.mp-auth-check {\n  display: flex;\n  align-items: flex-start;\n  gap: 10px;\n  font-size: 12.5px;\n  line-height: 1.5;\n  color: var(--muted);\n  cursor: pointer;\n}\n.mp-auth-check input[type='checkbox'] {\n  flex: 0 0 auto;\n  width: 15px;\n  height: 15px;\n  margin: 1px 0 0;\n  accent-color: var(--brand);\n  cursor: pointer;\n}\n.mp-auth-check a { color: var(--brand); font-weight: 600; text-decoration: none; }\n.mp-auth-check a:hover { text-decoration: underline; }\n\n/* ─── OTP boxes (OTPInput renders .rd-otp-group / .rd-otp-box) ─────────── */\n\n.mp-auth .rd-otp-group {\n  display: flex;\n  gap: 9px;\n  justify-content: space-between;\n  margin: 15px 0 2px;\n}\n.mp-auth .rd-otp-box {\n  flex: 1;\n  min-width: 0;\n  aspect-ratio: 1 / 1;\n  max-width: 56px;\n  text-align: center;\n  font-family: var(--mono);\n  font-size: 21px;\n  font-weight: 600;\n  color: var(--text);\n  background: var(--ink-2);\n  border: 1px solid var(--line-2);\n  border-radius: var(--r-sm);\n  outline: none;\n  transition: border-color 0.15s, box-shadow 0.15s;\n}\n.mp-auth .rd-otp-box:focus {\n  border-color: var(--brand);\n  box-shadow: 0 0 0 3px #7c6cff2e;\n}\n\n.mp-auth-resend {\n  margin: 18px 0 0;\n  font-size: 13px;\n  color: var(--faint);\n  text-align: center;\n}\n.mp-auth-timer { font-family: var(--mono); color: var(--brand); font-weight: 600; }\n\n/* Trust-this-device row. */\n.mp-auth-trust {\n  display: flex;\n  align-items: center;\n  gap: 10px;\n  flex-wrap: wrap;\n  margin: 18px 0 0;\n  padding: 12px 14px;\n  background: var(--ink-2);\n  border: 1px solid var(--line);\n  border-radius: var(--r-sm);\n  font-size: 13px;\n  color: var(--muted);\n  cursor: pointer;\n}\n.mp-auth-trust input[type='checkbox'] {\n  width: 16px;\n  height: 16px;\n  flex: 0 0 auto;\n  accent-color: var(--brand);\n  cursor: pointer;\n}\n.mp-auth-trust select {\n  height: 34px;\n  padding: 0 8px;\n  background: var(--surface-2);\n  border: 1px solid var(--line-2);\n  border-radius: 9px;\n  color: var(--text);\n  font-family: var(--body);\n  font-size: 12.5px;\n  font-weight: 600;\n  cursor: pointer;\n}\n\n/* ─── Statement panel (wide only) ──────────────────────────────────────────\n   Deliberately almost empty: a headline, one line, one trust signal. It exists\n   to make the page feel like a product rather than a form, and the weight comes\n   from type scale and the brand glow, not from copy.\n   ═════════════════════════════════════════════════════════════════════════ */\n\n.mp-auth-panel {\n  position: relative;\n  overflow: hidden;\n  padding: 52px 44px 44px;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  background: var(--ink-2);\n  color: var(--text);\n}\n\n/* Two offset orbs. The composition is the eye-catching part, so it gets to be\n   generous; there is no content underneath it to compete with. */\n.mp-auth-panel::before,\n.mp-auth-panel::after {\n  content: '';\n  position: absolute;\n  border-radius: 50%;\n  pointer-events: none;\n}\n.mp-auth-panel::before {\n  width: 420px;\n  height: 420px;\n  top: -140px;\n  right: -130px;\n  background: radial-gradient(50% 50%, #7c6cff8c, #a855f72e 45%, transparent 70%);\n  filter: blur(4px);\n}\n.mp-auth-panel::after {\n  width: 320px;\n  height: 320px;\n  bottom: -120px;\n  left: -100px;\n  background: radial-gradient(50% 50%, #a855f759, #7c6cff1f 48%, transparent 72%);\n}\n\n/* Hairline grid, barely there. A ledger surface rather than decoration. */\n.mp-auth-panel-grid {\n  position: absolute;\n  inset: 0;\n  background-image:\n    linear-gradient(#ffffff0a 1px, transparent 1px),\n    linear-gradient(90deg, #ffffff0a 1px, transparent 1px);\n  background-size: 46px 46px;\n  mask-image: radial-gradient(80% 70% at 50% 40%, #000, transparent 75%);\n  -webkit-mask-image: radial-gradient(80% 70% at 50% 40%, #000, transparent 75%);\n  pointer-events: none;\n}\n\n.mp-auth-panel-inner { position: relative; }\n\n.mp-auth-panel h2 {\n  margin: 18px 0 0;\n  font-family: var(--display);\n  font-size: 40px;\n  font-weight: 700;\n  line-height: 1.06;\n  letter-spacing: -0.04em;\n  color: #ffffff;\n  text-wrap: balance;\n}\n.mp-auth-panel h2 em {\n  font-style: normal;\n  background: linear-gradient(120deg, #cfc7ff, #e9c8ff);\n  -webkit-background-clip: text;\n  background-clip: text;\n  color: transparent;\n}\n\n.mp-auth-panel > .mp-auth-panel-inner > p {\n  margin: 16px 0 0;\n  font-size: 14.5px;\n  line-height: 1.62;\n  color: #c3c1d6;\n  max-width: 30ch;\n}\n\n.mp-auth-panel-note {\n  position: relative;\n  margin: 40px 0 0;\n  padding-top: 20px;\n  border-top: 1px solid #ffffff1f;\n  display: flex;\n  align-items: center;\n  gap: 9px;\n  font-size: 12px;\n  color: #a9a7c0;\n}\n.mp-auth-panel-note svg { flex: 0 0 auto; color: var(--mint); }\n\n/* ─── Responsive ───────────────────────────────────────────────────────── */\n\n@media (max-width: 979px) {\n  .mp-auth-card.is-wide { grid-template-columns: minmax(0, 1fr); max-width: 452px; }\n  .mp-auth-panel { display: none; }\n  .mp-auth-body { padding: 36px 30px 32px; }\n}\n\n@media (max-width: 520px) {\n  .mp-auth { padding: 0; align-items: stretch; }\n  .mp-auth-card,\n  .mp-auth-card.is-wide,\n  .mp-auth-card.is-narrow {\n    max-width: none;\n    border: 0;\n    border-radius: 0;\n    box-shadow: none;\n    min-height: 100vh;\n    align-content: center;\n  }\n  .mp-auth-body { padding: 30px 20px; }\n  .mp-auth-title { font-size: 22px; }\n  .mp-auth-row { grid-template-columns: minmax(0, 1fr); gap: 16px; }\n  .mp-auth .rd-otp-group { gap: 7px; }\n  .mp-auth .rd-otp-box { font-size: 18px; }\n}\n\n@media (prefers-reduced-motion: reduce) {\n  .mp-auth-btn,\n  .mp-auth-field,\n  .mp-auth-tabs a { transition: none; }\n  .mp-auth-btn.is-primary:hover:not(:disabled) { transform: none; }\n  .mp-auth-spin { animation-duration: 1.6s; }\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
